import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, FileText, Settings, RefreshCw, Trash2, Database } from "lucide-react";
import RailwayIntegration from "@/components/admin/RailwayIntegration";
import DocumentUpload from "@/components/admin/DocumentUpload";
import { useToast } from "@/hooks/use-toast";

interface KnowledgeDocument {
  id: string;
  title: string;
  content: string;
  file_name: string;
  file_type: string;
  file_size: number;
  user_id: string;
  created_at: string;
  updated_at: string;
  embeddings: string | null;
  processing_status?: string;
  chunk_count?: number | null;
  vector_dimensions?: number | null;
  processing_error?: string | null;
  processed_at?: string | null;
}

const AdminKnowledgeBase = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ['knowledge-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_documents')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as KnowledgeDocument[];
    }
  });

  const getStatusBadge = (doc: KnowledgeDocument) => {
    // If document has chunks and vector dimensions, it's completed
    if (doc.chunk_count && doc.vector_dimensions) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800">Completed</Badge>;
    }
    
    const status = doc.processing_status;
    switch (status) {
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'processing':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">Processing</Badge>;
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Document deleted",
        description: "The document has been removed from the knowledge base.",
      });

      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRetryProcessing = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_documents')
        .update({ 
          processing_status: 'pending',
          processing_error: null 
        })
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Processing requeued",
        description: "The document has been requeued for processing.",
      });

      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to retry processing. Please try again.",
        variant: "destructive",
      });
    }
  };

  const DocumentsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Knowledge Documents</h2>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading documents...</div>
      ) : (
        <div className="space-y-3">
          {documents?.map((doc) => (
            <Card key={doc.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <h3 className="font-medium">{doc.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {doc.file_name} • {(doc.file_size / 1024).toFixed(1)}KB
                        {doc.chunk_count && ` • ${doc.chunk_count} chunks`}
                        {doc.vector_dimensions && ` • ${doc.vector_dimensions}D vectors`}
                      </p>
                      {doc.processing_error && (
                        <p className="text-sm text-red-600 mt-1">
                          Error: {doc.processing_error}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {getStatusBadge(doc)}
                    
                    <div className="flex items-center gap-1">
                      {doc.processing_status === 'failed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRetryProcessing(doc.id)}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    
                    <span className="text-sm text-muted-foreground min-w-fit">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {(!documents || documents.length === 0) && (
            <Card>
              <CardContent className="text-center py-8 text-muted-foreground">
                <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No documents found in the knowledge base.</p>
                <p className="text-sm mt-2">Upload documents using the integrations below.</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Brain className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Knowledge Base Management</h1>
        </div>

        <Tabs defaultValue="documents" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Upload Documents
            </TabsTrigger>
          </TabsList>

          <TabsContent value="documents" className="mt-6">
            <DocumentsTab />
          </TabsContent>

          <TabsContent value="upload" className="mt-6">
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Document Upload</h2>
                <p className="text-muted-foreground mb-6">
                  Upload documents to be processed with Cohere embeddings and stored in Upstash vector database.
                </p>
              </div>
              <DocumentUpload />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminKnowledgeBase;